import { getButtonTranslations } from '@common/i18n/buttons';
import { DEFAULT_TEMPLATE_FORM_LANGUAGE } from '@common/i18n/constants';
import type { TemplateFormLanguage } from '@common/types/templateForms';
import { Alert, Button, Stack, useMediaQuery, useTheme } from '@mui/material';

interface Props {
    currentStep: number;
    numSteps: number;
    formSubmissionId: string | null;
    isDraftSaveEnabled?: boolean;
    isDraftSubmitDisabled: boolean;
    isNotAllRetainersSigned: boolean;
    isSubmitDisabled: boolean;
    handleDraftSubmit(): void;
    handleBackClick(): void;
    handleNextClick(): void;
    handleSubmitClick(): void;
    language?: TemplateFormLanguage;
}

export default function ButtonSection({
    currentStep,
    numSteps,
    formSubmissionId,
    isDraftSaveEnabled = true,
    isDraftSubmitDisabled,
    isNotAllRetainersSigned,
    isSubmitDisabled,
    handleDraftSubmit,
    handleBackClick,
    handleNextClick,
    handleSubmitClick,
    language = DEFAULT_TEMPLATE_FORM_LANGUAGE,
}: Props) {
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const t = getButtonTranslations(language);

    return (
        <Stack gap={1} padding={isMobile ? '12px 8px 8px 8px' : '12px 24px 24px 24px'}>
            <Stack direction={'row'} gap={2}>
                {!formSubmissionId && isDraftSaveEnabled && (
                    <Button
                        variant="contained"
                        sx={{
                            padding: isMobile ? '11px 18px' : '12px 24px',
                            textTransform: 'none',
                            width: isMobile && currentStep !== 0 ? '240px' : 'fit-content',
                        }}
                        disabled={isDraftSubmitDisabled}
                        onClick={handleDraftSubmit}
                    >
                        {t.saveAndReturnLater}
                    </Button>
                )}
                {/* Back button */}
                {currentStep !== 0 && (
                    <Button
                        variant={'outlined'}
                        sx={{
                            padding: '12px 24px',
                            textTransform: 'none',
                            width: 'auto',
                        }}
                        onClick={handleBackClick}
                    >
                        {t.back}
                    </Button>
                )}
                {/* Next button */}
                {currentStep < numSteps - 1 && (
                    <Button
                        variant={'contained'}
                        sx={{
                            padding: '12px 24px',
                            textTransform: 'none',
                            width: '144px',
                        }}
                        onClick={handleNextClick}
                    >
                        {t.next}
                    </Button>
                )}
                {/* Submit button */}
                {currentStep === numSteps - 1 && (
                    <Button
                        variant={'contained'}
                        disabled={isNotAllRetainersSigned || isSubmitDisabled}
                        sx={{
                            padding: '12px 24px',
                            textTransform: 'none',
                            width: '144px',
                        }}
                        onClick={handleSubmitClick}
                    >
                        {t.submit}
                    </Button>
                )}
            </Stack>
            {isNotAllRetainersSigned && (
                <Alert
                    sx={{
                        fontSize: '12px',
                        padding: '2px 10px',
                    }}
                    severity="info"
                >
                    {t.alerts.retainersNotSigned}
                </Alert>
            )}
            {isDraftSaveEnabled && isDraftSubmitDisabled && !formSubmissionId && (
                <Alert
                    sx={{
                        fontSize: '12px',
                        padding: '2px 10px',
                    }}
                    severity="info"
                >
                    {t.alerts.draftSaveRequirements}
                </Alert>
            )}
        </Stack>
    );
}
